import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { toast } from 'react-hot-toast';
import { 
  MessageSquare,
  FileQuestion,
  Video,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Send,
  X,
  Eye,
  User,
  Calendar,
  MessageCircle
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';

const TeacherRequestsManagement = () => {
  const { currentUser: admin } = useRealtime();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [discussionMessage, setDiscussionMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionType, setActionType] = useState(''); // 'reply', 'complete', 'reject', 'in_progress'

  const statusOptions = [
    { value: 'all', label: 'All Requests', color: 'text-gray-600', bgColor: 'bg-gray-100' },
    { value: 'pending', label: 'Pending', color: 'text-yellow-600', bgColor: 'bg-yellow-100', darkBgColor: 'dark:bg-yellow-900/20' },
    { value: 'in_progress', label: 'In Progress', color: 'text-blue-600', bgColor: 'bg-blue-100', darkBgColor: 'dark:bg-blue-900/20' },
    { value: 'approved', label: 'Approved', color: 'text-green-600', bgColor: 'bg-green-100', darkBgColor: 'dark:bg-green-900/20' },
    { value: 'completed', label: 'Completed', color: 'text-green-600', bgColor: 'bg-green-100', darkBgColor: 'dark:bg-green-900/20' },
    { value: 'rejected', label: 'Rejected', color: 'text-red-600', bgColor: 'bg-red-100', darkBgColor: 'dark:bg-red-900/20' }
  ];

  useEffect(() => {
    const q = query(
      collection(db, 'teacherRequests')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by creation date in memory (descending)
      requestsData.sort((a, b) => {
        const aTime = a.createdAt?.seconds || (a.createdAt?.getTime ? a.createdAt.getTime() / 1000 : 0);
        const bTime = b.createdAt?.seconds || (b.createdAt?.getTime ? b.createdAt.getTime() / 1000 : 0);
        return bTime - aTime;
      });
      
      setRequests(requestsData);
      setLoading(false);
    }, (error) => {
      toast.error('Failed to load requests');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const filteredRequests = requests.filter(request => {
    const matchesSearch = 
      request.teacherName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.batchName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.message?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleStatusUpdate = async (requestId, newStatus, responseMessage = '') => {
    setIsSubmitting(true);
    try {
      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };

      if (responseMessage) {
        updateData.adminResponse = responseMessage;
        updateData.respondedBy = admin?.uid || 'admin';
        updateData.respondedByName = admin?.displayName || admin?.email || 'Admin';
        updateData.respondedAt = serverTimestamp();
      }

      await updateDoc(doc(db, 'teacherRequests', requestId), updateData);
      toast.success(`Request ${newStatus === 'completed' ? 'marked as completed' : newStatus === 'rejected' ? 'rejected' : 'updated'} successfully!`);
      setShowDetailsModal(false);
      setAdminResponse('');
      setDiscussionMessage('');
      setActionType('');
    } catch (error) {
      toast.error('Failed to update request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddDiscussion = async (requestId) => {
    if (!discussionMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsSubmitting(true);
    try {
      const requestRef = doc(db, 'teacherRequests', requestId);
      const request = requests.find(r => r.id === requestId);
      
      const newDiscussion = {
        message: discussionMessage.trim(),
        authorName: admin?.displayName || admin?.email || 'Admin',
        authorId: admin?.uid || 'admin',
        createdAt: new Date() // Use regular Date instead of serverTimestamp() for arrays
      };

      const discussions = request.discussions || [];
      discussions.push(newDiscussion);

      await updateDoc(requestRef, {
        discussions: discussions,
        updatedAt: serverTimestamp()
      });

      toast.success('Discussion added successfully!');
      setDiscussionMessage('');
      
      // Update selected request to show new discussion
      setSelectedRequest({
        ...request,
        discussions: discussions
      });
    } catch (error) {
      toast.error('Failed to add discussion');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    if (!statusOption) return null;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        status === 'pending'
          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
          : status === 'in_progress'
          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
          : status === 'approved'
          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
          : status === 'completed'
          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
      }`}>
        {statusOption.label}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    let date;
    if (timestamp.seconds) {
      // Firestore timestamp
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      // Regular Date object
      date = timestamp;
    } else if (typeof timestamp === 'number') {
      // Unix timestamp
      date = new Date(timestamp);
    } else {
      // Try to parse as date
      date = new Date(timestamp);
    }
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Teacher Requests Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage test creation and video upload requests from teachers
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by teacher name, batch, or message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {requests.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {requests.filter(r => r.status === 'in_progress').length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">In Progress</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {requests.filter(r => r.status === 'completed' || r.status === 'approved').length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Completed</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {requests.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
          </div>
        </div>

        {/* Requests List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No requests found</h3>
              <p className="text-gray-600 dark:text-gray-400">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'No teacher requests at the moment'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRequests.map((request) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {request.requestType === 'test' ? (
                          <FileQuestion className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        ) : (
                          <Video className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        )}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {request.requestType === 'test' ? 'Test Creation' : 'Video Upload'} Request
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            by {request.teacherName} • Batch: {request.batchName}
                          </p>
                        </div>
                        {getStatusBadge(request.status)}
                      </div>
                      
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 line-clamp-2">
                        {request.message}
                      </p>

                      <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          <span>Created: {formatDate(request.createdAt)}</span>
                        </div>
                        {request.discussions && request.discussions.length > 0 && (
                          <div className="flex items-center">
                            <MessageCircle className="w-4 h-4 mr-1" />
                            <span>{request.discussions.length} discussion(s)</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowDetailsModal(true);
                        setActionType('');
                        setAdminResponse('');
                        setDiscussionMessage('');
                      }}
                      className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View</span>
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Request Details Modal */}
        {showDetailsModal && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {selectedRequest.requestType === 'test' ? (
                      <FileQuestion className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    ) : (
                      <Video className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    )}
                    <div>
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                        Request Details
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedRequest.requestType === 'test' ? 'Test Creation' : 'Video Upload'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedRequest(null);
                      setActionType('');
                      setAdminResponse('');
                      setDiscussionMessage('');
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                <div className="space-y-4">
                  {/* Request Info */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Teacher</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedRequest.teacherName}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Batch</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedRequest.batchName}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</p>
                        {getStatusBadge(selectedRequest.status)}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Created</p>
                        <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedRequest.createdAt)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Request Message */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Request Message:</p>
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{selectedRequest.message}</p>
                    </div>
                  </div>

                  {/* Admin Response */}
                  {selectedRequest.adminResponse && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Admin Response:</p>
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{selectedRequest.adminResponse}</p>
                      </div>
                    </div>
                  )}

                  {/* Discussions */}
                  {selectedRequest.discussions && selectedRequest.discussions.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Discussions:</p>
                      <div className="space-y-3">
                        {selectedRequest.discussions.map((discussion, index) => (
                          <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-gray-900 dark:text-white">
                                {discussion.authorName || 'Admin'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(discussion.createdAt)}
                              </p>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{discussion.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {selectedRequest.status !== 'completed' && selectedRequest.status !== 'rejected' && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <div className="flex flex-wrap gap-2 mb-4">
                        <button
                          onClick={() => setActionType(actionType === 'reply' ? '' : 'reply')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            actionType === 'reply'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          <Send className="w-4 h-4 inline mr-2" />
                          Add Discussion
                        </button>
                        <button
                          onClick={() => {
                            setActionType('in_progress');
                            handleStatusUpdate(selectedRequest.id, 'in_progress');
                          }}
                          disabled={isSubmitting || selectedRequest.status === 'in_progress'}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Clock className="w-4 h-4 inline mr-2" />
                          Mark In Progress
                        </button>
                        <button
                          onClick={() => setActionType(actionType === 'complete' ? '' : 'complete')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            actionType === 'complete'
                              ? 'bg-green-600 text-white'
                              : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/30'
                          }`}
                        >
                          <CheckCircle className="w-4 h-4 inline mr-2" />
                          Complete
                        </button>
                        <button
                          onClick={() => setActionType(actionType === 'reject' ? '' : 'reject')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            actionType === 'reject'
                              ? 'bg-red-600 text-white'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/30'
                          }`}
                        >
                          <XCircle className="w-4 h-4 inline mr-2" />
                          Reject
                        </button>
                      </div>

                      {/* Reply/Discussion Form */}
                      {actionType === 'reply' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Add Discussion Message
                          </label>
                          <textarea
                            value={discussionMessage}
                            onChange={(e) => setDiscussionMessage(e.target.value)}
                            placeholder="Enter your message..."
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                          />
                          <button
                            onClick={() => handleAddDiscussion(selectedRequest.id)}
                            disabled={isSubmitting || !discussionMessage.trim()}
                            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                          >
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                          </button>
                        </div>
                      )}

                      {/* Complete Form */}
                      {actionType === 'complete' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Completion Response (Optional)
                          </label>
                          <textarea
                            value={adminResponse}
                            onChange={(e) => setAdminResponse(e.target.value)}
                            placeholder="Add a completion message..."
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                          />
                          <button
                            onClick={() => handleStatusUpdate(selectedRequest.id, 'completed', adminResponse)}
                            disabled={isSubmitting}
                            className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                          >
                            {isSubmitting ? 'Completing...' : 'Mark as Completed'}
                          </button>
                        </div>
                      )}

                      {/* Reject Form */}
                      {actionType === 'reject' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Rejection Reason *
                          </label>
                          <textarea
                            value={adminResponse}
                            onChange={(e) => setAdminResponse(e.target.value)}
                            placeholder="Please provide a reason for rejection..."
                            rows={3}
                            required
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                          />
                          <button
                            onClick={() => {
                              if (!adminResponse.trim()) {
                                toast.error('Please provide a rejection reason');
                                return;
                              }
                              handleStatusUpdate(selectedRequest.id, 'rejected', adminResponse);
                            }}
                            disabled={isSubmitting || !adminResponse.trim()}
                            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                          >
                            {isSubmitting ? 'Rejecting...' : 'Reject Request'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherRequestsManagement;

